import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  changeHuntPackStatusAPI,
  createHuntPackAPI,
  deleteHuntPackAPI,
  fetchHuntPackListAPI,
  updateHuntPackAPI,
} from "../apis/huntPack.api.js";
import { errorToastify, successToastify } from "../utils/toastify.util.js";
import { getApiErrorMessage } from "../utils/upload.util.js";

const initialState = {
  isLoad: false,
  isBtnLoad: false,
  isStatusLoad: false,
  isRedirect: false,
  listData: [],
  totalCount: 0,
  lastQuery: {},
  selectedHuntPack: null,
  deleteHuntPackId: null,
};

export const fetchHuntPackList = createAsyncThunk(
  "huntPack/fetchList",
  async (payload, thunkAPI) => {
    try {
      const response = await fetchHuntPackListAPI(payload);
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const createHuntPack = createAsyncThunk(
  "huntPack/create",
  async (payload, thunkAPI) => {
    try {
      const response = await createHuntPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.huntPack?.lastQuery || {};
      thunkAPI.dispatch(fetchHuntPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const updateHuntPack = createAsyncThunk(
  "huntPack/update",
  async (payload, thunkAPI) => {
    try {
      const response = await updateHuntPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.huntPack?.lastQuery || {};
      thunkAPI.dispatch(fetchHuntPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const changeHuntPackStatus = createAsyncThunk(
  "huntPack/changeStatus",
  async (payload, thunkAPI) => {
    try {
      const response = await changeHuntPackStatusAPI(payload);
      const lastQuery = thunkAPI.getState()?.huntPack?.lastQuery || {};
      thunkAPI.dispatch(fetchHuntPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

export const deleteHuntPack = createAsyncThunk(
  "huntPack/delete",
  async (payload, thunkAPI) => {
    try {
      const response = await deleteHuntPackAPI(payload);
      const lastQuery = thunkAPI.getState()?.huntPack?.lastQuery || {};
      thunkAPI.dispatch(fetchHuntPackList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  },
);

const huntPackSlice = createSlice({
  name: "huntPack",
  initialState,
  reducers: {
    setHuntPackRedirect: (state, { payload }) => {
      state.isRedirect = payload;
    },
    setSelectedHuntPack: (state, { payload }) => {
      state.selectedHuntPack = payload;
    },
    setDeleteHuntPackId: (state, { payload }) => {
      state.deleteHuntPackId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHuntPackList.pending, (state, action) => {
      state.isLoad = true;
      state.lastQuery = action.meta?.arg || {};
    });
    builder.addCase(fetchHuntPackList.fulfilled, (state, action) => {
      state.isLoad = false;
      if (action.payload?.success) {
        state.listData = action.payload?.result?.list || [];
        state.totalCount = Number(action.payload?.result?.totalCount || 0);
      } else {
        errorToastify(action.payload?.message || "Failed to fetch hunt packs");
      }
    });
    builder.addCase(fetchHuntPackList.rejected, (state, action) => {
      state.isLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(createHuntPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(createHuntPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.isRedirect = true;
        const resultStatus = Number(action.payload?.result?.status);
        const savedAsDraft =
          !Number.isNaN(resultStatus) && action.payload?.result?.status != null
            ? resultStatus === 3
            : Number(action.meta?.arg?.status) === 3;
        successToastify(
          savedAsDraft
            ? "Hunt pack saved as draft"
            : "Hunt pack published to store",
        );
      } else {
        errorToastify(action.payload?.message || "Failed to create hunt pack");
      }
    });
    builder.addCase(createHuntPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(updateHuntPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(updateHuntPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.isRedirect = true;
        const savedAsDraft = Number(action.meta?.arg?.status) === 3;
        successToastify(
          savedAsDraft
            ? "Hunt pack saved as draft"
            : "Hunt pack published to store",
        );
      } else {
        errorToastify(action.payload?.message || "Failed to update hunt pack");
      }
    });
    builder.addCase(updateHuntPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(changeHuntPackStatus.pending, (state) => {
      state.isStatusLoad = true;
    });
    builder.addCase(changeHuntPackStatus.fulfilled, (state, action) => {
      state.isStatusLoad = false;
      if (action.payload?.success) {
        successToastify("Hunt Pack status updated successfully");
      } else {
        errorToastify(action.payload?.message || "Failed to update hunt pack status");
      }
    });
    builder.addCase(changeHuntPackStatus.rejected, (state, action) => {
      state.isStatusLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(deleteHuntPack.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(deleteHuntPack.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      if (action.payload?.success) {
        state.deleteHuntPackId = null;
        successToastify("Hunt Pack deleted successfully");
      } else {
        errorToastify(action.payload?.message || "Failed to delete hunt pack");
      }
    });
    builder.addCase(deleteHuntPack.rejected, (state, action) => {
      state.isBtnLoad = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });
  },
});

export const {
  setHuntPackRedirect,
  setSelectedHuntPack,
  setDeleteHuntPackId,
} = huntPackSlice.actions;

export default huntPackSlice.reducer;
